import { Box, Typography, Link as MuiLink } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { inlineTokens, internalPath, tableAt } from '../lib/markdown.js';

/**
 * Minimal, dependency-free renderer for the markdown our AI writing uses:
 * headings, `- ` / `1. ` lists, pipe tables, `[links](…)`, `**bold**` and
 * inline `` `code` ``. Blank lines separate paragraphs.
 *
 * It stays hand-rolled rather than pulling in a parser because the input is
 * ours — summaries and Ask answers — and the subset is small. What it must not
 * do is render markdown it does not understand as punctuation: a table printed
 * as rows of pipes is worse than no table, which is what the Ask page shipped
 * with before this.
 */

function Anchor({ href, children }: { href: string; children: ReactNode }) {
  const navigate = useNavigate();
  const inApp = internalPath(href, window.location.origin);
  return (
    <MuiLink
      href={href}
      {...(inApp
        ? {
            onClick: (e: React.MouseEvent) => {
              // Let the browser handle a deliberate new-tab click.
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              navigate(inApp);
            },
          }
        : { target: '_blank', rel: 'noopener noreferrer' })}
      sx={{ wordBreak: 'break-word' }}
    >
      {children}
    </MuiLink>
  );
}

/** Turns one line's inline tokens into nodes; recurses through link labels. */
function renderInline(text: string, keyBase: string): ReactNode[] {
  return inlineTokens(text).map((t, i) => {
    if (t.kind === 'text') return t.text;
    if (t.kind === 'bold') return <strong key={`${keyBase}-b${i}`}>{t.text}</strong>;
    if (t.kind === 'code') {
      return (
        <Box
          key={`${keyBase}-c${i}`}
          component="code"
          sx={{
            px: 0.6,
            py: 0.1,
            borderRadius: 1,
            bgcolor: 'action.hover',
            fontFamily: 'ui-monospace, monospace',
            fontSize: '0.85em',
            wordBreak: 'break-all',
          }}
        >
          {t.text}
        </Box>
      );
    }
    // A refused href keeps its label, so the sentence still reads.
    return t.href ? (
      <Anchor key={`${keyBase}-a${i}`} href={t.href}>
        {renderInline(t.label, `${keyBase}-a${i}l`)}
      </Anchor>
    ) : (
      t.label
    );
  });
}

const HEADING_VARIANT = ['h5', 'h6', 'subtitle1', 'subtitle2', 'subtitle2', 'subtitle2'] as const;

export function Markdown({ text }: { text: string }) {
  const lines = text.split('\n');
  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; items: ReactNode[] } = { ordered: false, items: [] };

  const flush = () => {
    if (!list.items.length) return;
    blocks.push(
      <Box
        component={list.ordered ? 'ol' : 'ul'}
        key={`l${blocks.length}`}
        sx={{ m: 0, my: 0.5, pl: 2.5 }}
      >
        {list.items}
      </Box>,
    );
    list = { ordered: false, items: [] };
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;

    // A table is the one construct that needs its next line to be recognised,
    // so it is read ahead rather than line by line.
    const table = tableAt(lines, i);
    if (table) {
      flush();
      const { head, body } = table;
      blocks.push(
        <Box key={`t${i}`} sx={{ overflowX: 'auto', my: 1 }}>
          <Box
            component="table"
            sx={{
              borderCollapse: 'collapse',
              width: '100%',
              fontSize: '0.875rem',
              '& th, & td': {
                border: '1px solid',
                borderColor: 'divider',
                px: 1,
                py: 0.5,
                textAlign: 'left',
                verticalAlign: 'top',
              },
              '& th': { bgcolor: 'action.hover', fontWeight: 600 },
            }}
          >
            <Box component="thead">
              <Box component="tr">
                {head.map((c, k) => (
                  <Box component="th" key={k}>
                    {renderInline(c, `th${i}-${k}`)}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {body.map((row, r) => (
                <Box component="tr" key={r}>
                  {row.map((c, k) => (
                    <Box component="td" key={k}>
                      {renderInline(c, `td${i}-${r}-${k}`)}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>,
      );
      i = table.end - 1;
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      flush();
      blocks.push(
        <Typography
          key={`h${i}`}
          variant={HEADING_VARIANT[heading[1]!.length - 1]}
          sx={{ mt: blocks.length ? 1.5 : 0, mb: 0.75, fontWeight: 700 }}
        >
          {renderInline(heading[2]!, `h${i}`)}
        </Typography>,
      );
      continue;
    }

    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (bullet || numbered) {
      const ordered = !bullet;
      // A list that changes kind mid-run starts a new list rather than
      // silently renumbering under the wrong marker.
      if (list.items.length && list.ordered !== ordered) flush();
      list.ordered = ordered;
      list.items.push(
        <Box component="li" key={`li${i}`} sx={{ mb: 0.5, lineHeight: 1.6 }}>
          <Typography variant="body2" component="span">
            {renderInline((bullet ?? numbered)![1]!, `l${i}`)}
          </Typography>
        </Box>,
      );
      continue;
    }

    if (line.trim() === '') {
      flush();
      continue;
    }

    flush();
    blocks.push(
      <Typography key={`p${i}`} variant="body2" sx={{ mb: 1, lineHeight: 1.7 }}>
        {renderInline(line, `p${i}`)}
      </Typography>,
    );
  }
  flush();

  return <Box>{blocks}</Box>;
}
